import { reportBelongsToSite } from '../../../../lib/reportSiteIdentity';
import { UNAVAILABLE_COMPOSITE_ANALYSIS_CONTEXT } from '../../../../lib/portfolio/compositeScorer';
/**
 * ExecutiveDecisionGuideSection (execDecisionBand) — the Go/No-Go Decision Guide
 * band. It PREFERS the gen-time snapshot (`report.executiveDecisionRollupAtGen`,
 * stamped by runTypedReportGeneration) so the live viewer and the print/export PDF
 * are byte-identical — the print subtree has a COLD store, so a live recompute
 * blanked the band in the PDF (Report P0). It recomputes live from the store's
 * CURRENT reports / intent / evidence ONLY as the legacy fallback for reports
 * stamped before the snapshot field existed.
 *
 * READ-ONLY over the deterministic `buildExecutiveDecisionRollup` engine
 * (deriveSiteVerdict · getDisplayVerdict · pathToGo) — themed via roleColor()
 * exactly like the Financial DecisionBandSection (the pattern this copies):
 * the verdict plate, the per-source verdict matrix, the binding constraints
 * that held the band down, the auditable rationale, and an engine-provenance
 * chip. No bespoke hardcoded colors; verdict tone maps through roleColor
 * positive / warning / negative; the page stays white via the report's
 * `data-theme-scope='light'` island.
 *
 * W2c editorial port — the hand-rolled band + mono uppercase group labels move
 * onto the editorial primitives while the decision-band SEMANTICS (verdict-
 * toned left rule + wash plate, label / coverage / provenance content) and the
 * testids are preserved exactly:
 *   - verdict plate → EditorialCallout (tone = band tone — the primitive IS
 *     the left-rule + soft-wash band treatment). EditorialSectionHeader is NOT
 *     used: SectionShell owns this section's canonical head (the primitive's
 *     own docstring scopes it to sections that render their own head), and an
 *     EditorialKpiRail would relocate the coverage figure out of the band —
 *     a content-placement change this presentation-only port avoids.
 *   - per-source matrix / binding constraints / rationale → EditorialSubsection
 *     (small-caps label chrome); the toned list rows are unchanged content.
 *
 * LIVE DATA-FLOW (mirrors SiteLandingPage): the router only
 * passes `report` + `label`, so this section reads its live inputs straight from
 * the store via `useStore`, honoring the project's Zustand rule — subscribe to
 * ROOTS (s.siteRag.reports, s.portfolio) and derive the per-site slices in a
 * useMemo, never `.filter` inside a selector. The composite is computed lazily
 * (computeCompositeForSite) on mount when the store cache is cold.
 */
import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import { SectionShell } from '../SectionShell';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import {
  EditorialBadge,
  EditorialCallout,
  EditorialSubsection,
  type EditorialBadgeTone,
  type EditorialCalloutTone,
} from '../../report/editorial';
import { useStore } from '../../../../store';
import { REPORT_TYPE_REGISTRY } from '../../../../config/reportTypes';
import {
  buildExecutiveDecisionRollup,
  type DerivedBand,
  type DecisionGuideRollup,
} from '../../../../lib/executiveDecisionRollup';
import { isSiteCompositeStale } from '../../../../lib/portfolio/compositeScorer';
import {
  decisionSnapshotStaleness,
  describeDecisionSnapshotStaleness,
} from '../../../../lib/reportGeneration/decisionSnapshotStaleness';
import type { VerdictTier } from '../../../../lib/reportVerdict';
import { GenerationComparisonBlock, GenerationGoNoGoBlock, GenerationScreeningEconomicsBlock } from '../../report/GenerationImpactProjectionBlocks';

const FIN_MONO = 'var(--report-body-font, inherit)';

/** Band → editorial tone (positive / warning / negative / neutral). */
function bandTone(band: DerivedBand): EditorialBadgeTone {
  if (band === 'go') return 'positive';
  if (band === 'conditional-high' || band === 'conditional') return 'warning';
  if (band === 'no-go') return 'negative';
  return 'neutral';
}
const TONE_ROLE: Record<EditorialBadgeTone, 'positive' | 'warning' | 'negative' | 'accent'> = {
  positive: 'positive',
  warning: 'warning',
  negative: 'negative',
  neutral: 'accent',
  accent: 'accent',
};

/** Tier → editorial tone for the per-source matrix dots. */
function tierTone(tier: VerdictTier): EditorialBadgeTone {
  if (tier === 'green') return 'positive';
  if (tier === 'amber') return 'warning';
  if (tier === 'red') return 'negative';
  return 'neutral';
}

export const ExecutiveDecisionGuideSection: React.FC<{
  report: SiteRagReport;
  label: string;
}> = ({ report, label }) => {
  const theme = useEffectiveReportTheme();

  // ── LIVE inputs — subscribe to ROOTS, derive per-site slices in useMemo. ─────
  const allReports = useStore((s) => s.siteRag.reports);
  const sites = useStore((s) => s.siteRag.sites);
  const composites = useStore((s) => s.portfolio?.composites);
  const analysisContexts = useStore((s) => s.portfolio?.analysisContexts);
  const bandConfig = useStore((s) => s.portfolio?.bandConfig ?? null);
  const computeCompositeForSite = useStore((s) => s.computeCompositeForSite);

  // The site this report belongs to (the canonical live source the verdict
  // surfaces read — matches SiteRagReportView's reportSite resolution).
  const site = React.useMemo(
    () => (report.siteId ? sites.find((x) => x.id === report.siteId) ?? null : null),
    [sites, report.siteId],
  );

  const analysisContext = (site ? analysisContexts?.[site.id] : undefined) ?? UNAVAILABLE_COMPOSITE_ANALYSIS_CONTEXT;

  // ALL sibling reports for the site — match by siteId / reportIds / siteName
  // (mirrors the SiteLandingPage filter rule so the rollup sees every source).
  const siteReports = React.useMemo<SiteRagReport[]>(() => {
    if (!site) return [];
    return allReports.filter(
      (r) =>
        reportBelongsToSite(site, r),
    );
  }, [allReports, site]);

  const composite = React.useMemo(
    () => (site ? composites?.[site.id] ?? null : null),
    [composites, site],
  );

  // Lazily compute the composite when the store cache is cold (mirrors
  // SiteLandingPage's effect). Guarded so it fires at most once per site id.
  const requestedRef = React.useRef<Record<string, boolean>>({});
  React.useEffect(() => {
    if (!site) return;
    if (typeof computeCompositeForSite !== 'function') return;
    // RC-1: recompute on stale-OR-absent (mirrors SiteLandingPage) so the Decision Guide reflects
    // freshly-generated reports, not the pre-report cached composite. Latch cleared on completion.
    // M-tier fingerprint (2026-07-02): current inputs (sibling reports + site SED pointer) ride
    // along — unchanged inputs read as fresh regardless of wall-clock age.
    if (!isSiteCompositeStale(composite, undefined, { site, reports: siteReports, analysisContext })) return;
    if (requestedRef.current[site.id]) return;
    requestedRef.current[site.id] = true;
    void computeCompositeForSite(site.id)
      // Swallow first (best-effort recompute) so a failed/stubbed compute can't reject unhandled, THEN
      // clear the in-flight latch on completion (success or failure) so a later stale-stamp re-triggers.
      .catch(() => {})
      .finally(() => {
        requestedRef.current[site.id] = false;
      });
  }, [site, siteReports, composite, computeCompositeForSite, analysisContext]);

  const rollup = React.useMemo<DecisionGuideRollup | null>(() => {
    // Report P0 — prefer the gen-time snapshot (present on new exec-summary reports). The print/export
    // subtree has a COLD store, so a live recompute would blank out the PRIMARY decision band; reading
    // the snapshot makes the live viewer and the PDF byte-identical. Live recompute remains the legacy
    // fallback ONLY for reports stamped before this field existed.
    if (report.executiveDecisionRollupAtGen) return report.executiveDecisionRollupAtGen;
    if (!site) return null;
    return buildExecutiveDecisionRollup({
      site,
      sed: null,
      reports: siteReports,
      intent: site.projectIntent,
      bandConfig,
      composite,
      analysisContext,
    });
  }, [report, site, siteReports, bandConfig, composite, analysisContext]);

  const generationSummary = report.generationImpactProjection?.goNoGoSummary;
  const hasData = Boolean(rollup || generationSummary);
  if (!rollup) {
    return (
      <SectionShell label={label} hasData={hasData} report={report}>
        <GenerationGoNoGoBlock report={report} />
        <GenerationComparisonBlock report={report} />
        <GenerationScreeningEconomicsBlock report={report} />
      </SectionShell>
    );
  }

  // Part I S4 — STALE render-guard. When the LIVE composite is stale (a report
  // changed its inputs and the recompute effect above has not landed yet) the
  // rollup is built from the PRE-REPORT composite — showing its verdict/coverage
  // would assert wrong/zero scores on a site that HAS reports. Gate ONLY when a
  // real recompute is actually pending: a stale-or-absent composite on a site
  // that HAS reports. A site with NO reports has nothing to recompute, so it
  // falls through to the honest "Insufficient data" withhold below (never a
  // misleading "Recomputing…"). The gen-time snapshot path
  // (`executiveDecisionRollupAtGen`, used by print/export with a cold store) is
  // intentionally frozen and is NOT gated here.
  const usingGenSnapshot = Boolean(report.executiveDecisionRollupAtGen);
  const recomputePending = siteReports.length > 0
    && isSiteCompositeStale(composite, undefined, site ? { site, reports: siteReports, analysisContext } : null);
  if (!usingGenSnapshot && recomputePending) {
    return (
      <SectionShell
        label={label}
        hasData={Boolean(generationSummary)}
        required
        report={report}
        emptyMessage="Recomputing scores… a report changed the inputs to this site's decision verdict."
      >
        {generationSummary && (
          <EditorialCallout theme={theme} tone="accent" label="Overall site decision updating">
            The promoted generation scenario remains available below while the cross-report Go / No-Go determination is recomputed.
          </EditorialCallout>
        )}
        <GenerationGoNoGoBlock report={report} />
        <GenerationComparisonBlock report={report} />
        <GenerationScreeningEconomicsBlock report={report} />
      </SectionShell>
    );
  }

  // P1.4b — the frozen snapshot is kept (the print subtree has a cold store
  // and must not blank the band), but a snapshot the site's evidence has moved
  // past is SAID so: decision-source siblings generated after this report. In
  // the print subtree `siteReports` is empty, so the note cannot fire there;
  // the office exporters carry the same note from the sibling list they are
  // handed (`siblingReports`).
  const frozenStaleNote = usingGenSnapshot
    ? describeDecisionSnapshotStaleness(report, decisionSnapshotStaleness(report, siteReports))
    : null;

  const verdictRole: EditorialCalloutTone = TONE_ROLE[bandTone(rollup.band)];
  const verdictInk = roleColor(theme, verdictRole);
  const coveragePct = Math.round(rollup.adequacy.coverageRatio * 100);
  // P2.3 — beside a WITHHELD label the pillar-coverage figure is the wrong
  // number: it is not the floor that bound (the weight floor, a conflict, or
  // an undeclared strategy), and it reads as healthy next to a withhold —
  // and it is frozen into the exported PDF. Name what bound instead.
  const withheldWhy = rollup.band === null
    ? (rollup.adequacy.reason ?? rollup.bindingConstraints[0] ?? null)
    : null;

  return (
    <SectionShell label={label} hasData={hasData} report={report}>
      <div className="space-y-4">
        {frozenStaleNote && (
          <div data-testid="exec-decision-band-stale">
            <EditorialCallout theme={theme} tone="warning" label="Decision basis has moved">
              {frozenStaleNote}
            </EditorialCallout>
          </div>
        )}
        {/* Verdict plate — EditorialCallout carries the band semantics (verdict-
            toned left rule + soft wash, all theme-derived). Carrier div keeps
            the decision-band testid (carrier-div convention). */}
        <div data-testid="exec-decision-band">
          <EditorialCallout theme={theme} tone={verdictRole}>
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span
                className="text-xl font-bold"
                style={{ color: verdictInk }}
                data-testid="exec-verdict-label"
              >
                {rollup.label}
              </span>
              <span
                className="text-sm"
                style={{ fontFamily: FIN_MONO, color: roleColor(theme, 'muted') }}
              >
                {withheldWhy ?? `${coveragePct}% pillar coverage`}
              </span>
              <span className="grow" />
              <EditorialBadge
                theme={theme}
                tone="neutral"
                variant="outline"
                title="Deterministic methodology: evidence coverage, source verdicts, and readiness gates; no generative model sets the result."
              >
                Evidence coverage · source verdicts · readiness gates
              </EditorialBadge>
            </div>
          </EditorialCallout>
        </div>

        <GenerationGoNoGoBlock report={report} />
        <GenerationComparisonBlock report={report} />
        <GenerationScreeningEconomicsBlock report={report} />

        {/* Per-source verdict matrix — the mandatory diligence sources + tier. */}
        {rollup.perSourceVerdicts.length > 0 ? (
          <EditorialSubsection theme={theme} label="Mandatory source verdicts">
            <ul className="space-y-1" data-testid="exec-source-matrix">
              {rollup.perSourceVerdicts.map((row) => {
                const tone = tierTone(row.tier);
                const ink = roleColor(theme, TONE_ROLE[tone]);
                const typeLabel =
                  REPORT_TYPE_REGISTRY[row.reportType]?.shortLabel ?? row.reportType;
                return (
                  <li
                    key={`src-${row.reportType}`}
                    className="text-sm flex items-baseline gap-2"
                    style={{ color: roleColor(theme, 'body') }}
                  >
                    <span aria-hidden style={{ color: ink }}>
                      {'●'}
                    </span>
                    <span className="font-medium">{typeLabel}</span>
                    <span className="grow" />
                    <span style={{ color: row.present && row.qualifies !== false ? ink : roleColor(theme, 'muted') }}>
                      {!row.present
                        ? 'Not yet generated'
                        : row.qualifies === false
                          ? 'Refresh needed'
                          : row.label}
                    </span>
                  </li>
                );
              })}
            </ul>
          </EditorialSubsection>
        ) : null}

        {/* CONNECTIVITY-ADVISORY — informational source verdicts. Displayed so
            an existing advisory report never silently vanishes from the guide,
            but these rows are structurally excluded from every cap/floor path
            (`advisoryVerdicts` is never iterated by the rollup engine). The
            `?? []` guard covers frozen pre-change rollup snapshots. */}
        {(rollup.advisoryVerdicts ?? []).length > 0 ? (
          <EditorialSubsection
            theme={theme}
            label="Advisory analyses (informational — not counted in the go/no-go)"
          >
            <ul className="space-y-1" data-testid="exec-advisory-matrix">
              {(rollup.advisoryVerdicts ?? []).map((row) => {
                const typeLabel =
                  REPORT_TYPE_REGISTRY[row.reportType]?.shortLabel ?? row.reportType;
                return (
                  <li
                    key={`adv-${row.reportType}`}
                    className="text-sm flex items-baseline gap-2"
                    style={{ color: roleColor(theme, 'body') }}
                  >
                    <span
                      aria-hidden
                      data-pdf-advisory-status-mark="true"
                      style={{
                        alignSelf: 'center',
                        border: '1.5px solid currentColor',
                        borderRadius: '9999px',
                        boxSizing: 'border-box',
                        color: roleColor(theme, 'muted'),
                        display: 'inline-block',
                        flexShrink: 0,
                        height: 8,
                        width: 8,
                      }}
                    />
                    <span className="font-medium">{typeLabel}</span>
                    <span className="grow" />
                    <span style={{ color: roleColor(theme, 'muted') }}>
                      {row.label}
                      {' — advisory'}
                    </span>
                  </li>
                );
              })}
            </ul>
          </EditorialSubsection>
        ) : null}

        {/* Binding constraints — what held the band down (empty when GO). */}
        {rollup.bindingConstraints.length > 0 ? (
          <EditorialSubsection theme={theme} label="Binding constraints">
            <ul className="space-y-1" data-testid="exec-binding-constraints">
              {rollup.bindingConstraints.map((c, i) => (
                <li
                  key={`bind-${i}`}
                  className="text-sm flex gap-2"
                  style={{ color: roleColor(theme, 'body') }}
                >
                  <span aria-hidden style={{ color: roleColor(theme, 'negative') }}>
                    {'•'}
                  </span>
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </EditorialSubsection>
        ) : null}

        {/* Auditable rationale — why the band landed where it did. */}
        {rollup.rationale.length > 0 ? (
          <EditorialSubsection theme={theme} label="Rationale">
            <ul className="space-y-1">
              {rollup.rationale.map((r, i) => (
                <li
                  key={`rat-${i}`}
                  className="text-sm flex gap-2"
                  style={{ color: roleColor(theme, 'body') }}
                >
                  <span aria-hidden style={{ color: roleColor(theme, 'accent') }}>
                    {'–'}
                  </span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </EditorialSubsection>
        ) : null}
      </div>
    </SectionShell>
  );
};
